import { useEffect, useRef } from 'react';

const POLL_INTERVAL = 5000; // 5 seconds

/**
 * Reusable polling hook for async export report status.
 *
 * Polls the backend every 5 seconds while `isPolling` is true.
 * On completion (status 10) or error (status -1), calls the
 * appropriate callback and stops polling automatically.
 *
 * @param {Object} options
 * @param {boolean}  options.isPolling    - Whether polling is active
 * @param {Function} options.fetchStatus  - Async fn that returns the API response
 * @param {Function} options.markSeen     - Async fn(reportId) to mark report as seen
 * @param {Function} options.onComplete   - Callback when report is ready (receives file_url)
 * @param {Function} options.onError      - Callback when report fails (receives error_notes)
 * @param {Function} options.onStop       - Callback to flip isPolling back to false
 */
const useExportReportPolling = ({
  isPolling,
  fetchStatus,
  markSeen,
  onComplete,
  onError,
  onStop,
}) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isPolling) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const res = await fetchStatus();

        if (res?.status !== 200) return;

        const report = res.data?.export_report;
        if (!report) {
          onStop?.();
          return;
        }

        // COMPLETED
        if (report.status === 10) {
          await markSeen(report.id);
          onComplete?.(report.file_url);
          onStop?.();
        }

        // ERROR
        if (report.status === -1) {
          await markSeen(report.id);
          onError?.(report.error_notes);
          onStop?.();
        }
      } catch (err) {
        console.error('Export report polling error:', err);
      }
    };

    // First poll immediately
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling]);

  return null;
};

export default useExportReportPolling;
